// Single source of truth for backend-field → product-facing labels.
//
// Mirrors policyName()/POLICY_NAMES in personaContext.ts: one registry plus a
// page-aware resolver. Charts and tables read fieldLabel()/fieldShort() instead
// of hardcoding raw `model_*` column names, so exec-facing surfaces speak
// business language while the raw key stays available (subcaption/tooltip) for
// traceability. PageKey intentionally reuses the PAGE_PERSONA page keys.

export type PageKey = 'valueOpportunity' | 'activePlanning' | 'productStockingLocations' | 'backtest';

export interface FieldMeta {
  // Full business-facing label.
  label: string;
  // Compact label for legends / axes / chips.
  short?: string;
  unit?: 'usd' | 'units' | 'days' | 'weeks' | 'ratio' | 'pct';
  // Optional hover/tooltip copy.
  description?: string;
}

// Shared base — a field carries the same meaning across pages. Keyed by the
// backend field/column name (or historical-performance metric key).
export const FIELD_LABELS: Record<string, FieldMeta> = {
  // --- Free Cash Flow build-up (Value Opportunity) ---
  model_fcf: { label: 'Free Cash Flow (model)', short: 'FCF', unit: 'usd' },
  model_revenue_demand_fulfilled: { label: 'Revenue fulfilled (demand)', short: 'Revenue fulfilled', unit: 'usd' },
  model_revenue_backlog_fulfilled: { label: 'Revenue recovered (backlog)', short: 'Backlog recovered', unit: 'usd' },
  model_order_placed_total_cost: { label: 'Order / production cost', short: 'Order cost', unit: 'usd' },
  model_backlog_total_penalty_cost: { label: 'Late-delivery penalty', short: 'Penalty', unit: 'usd' },
  model_salvage_value: { label: 'Salvage value', short: 'Salvage', unit: 'usd' },
  model_ending_onhand_value: { label: 'Average inventory (at cost)', short: 'Avg inventory', unit: 'usd' },
  days_of_supply: { label: 'Days of Supply', short: 'DoS', unit: 'days' },
  available_in_full_per_period: { label: 'Availability in full', short: 'Avail. in full', unit: 'pct' },

  // --- Inventory coverage (Product / Stocking Locations) ---
  model_beginning_available: { label: 'On-hand available', short: 'On-hand', unit: 'units' },
  model_beginning_inflight: { label: 'In-transit (inflight)', short: 'In-transit', unit: 'units' },
  model_ending_inflight: { label: 'In-transit (inflight after Reorder)', short: 'In-transit', unit: 'units' },
  model_order_constrained: { label: 'Reorder quantity', short: 'Reorder', unit: 'units' },
  model_expected_demand_over_lt_ph: { label: 'Expected demand', short: 'Demand', unit: 'units' },
  model_projected_inv_over_lt_ph: { label: 'Projected inventory position', short: 'Projected inv.', unit: 'units' },
  model_projected_inv: { label: 'Ending inventory position', short: 'Ending inv.', unit: 'units' },
  total_lead_time: { label: 'Lead time', short: 'Lead time', unit: 'weeks' },
  periods_between_replenishment: { label: 'Review cycle (PBR)', short: 'PBR', unit: 'weeks' },
  model_target_inv_pos: { label: 'Target inventory position', short: 'Target', unit: 'units' },
  model_target_service_level: { label: 'Target service level', short: 'Target SL', unit: 'pct' },
  min_order_size: { label: 'Minimum order size', short: 'Min order', unit: 'units' },

  // --- BackTest / Historical Performance ---
  fill_rate_beta_rev: { label: 'Fill rate (revenue-weighted)', short: 'Fill rate $', unit: 'ratio' },
  fill_rate_beta_qty: { label: 'Fill rate (units)', short: 'Fill rate u', unit: 'ratio' },
  fill_rate_alpha_per_period_qty: { label: 'Weekly in-full', short: 'In-full', unit: 'ratio' },
  fcf_cumulative_vs_ref: { label: 'Cumulative FCF vs Incumbent', short: 'Cum. FCF', unit: 'usd' },
  hist_demand: { label: 'Actual demand', short: 'Demand', unit: 'units' },
  fcf_impact: { label: 'FCF impact', short: 'FCF impact', unit: 'usd' },
  net_fcf_impact: { label: 'Net FCF impact across all SKU × Site', short: 'Net FCF impact', unit: 'usd' },
  skus_evaluated: { label: 'SKUs evaluated', short: 'SKUs', unit: 'units' },
  policy_count: { label: 'Policies compared', short: 'Policies' },
};

// Per-page wording tweaks layered over the base. Sparse — most fields are
// page-invariant; only override where a page genuinely needs different copy.
export const PAGE_FIELD_OVERRIDES: Partial<Record<PageKey, Record<string, Partial<FieldMeta>>>> = {
  valueOpportunity: {
    // The contributors table frames hist_demand as the requested (unconstrained)
    // demand the policies are scored against.
    hist_demand: { label: 'Requested demand', short: 'Requested demand' },
  },
  // Product / Stocking Locations naming (issue #125). Scoped to this page so the
  // longer, customer-facing names don't ripple into Active Planning / Value Opp.
  // "P90" is hardcoded pending the forecast-percentile ticket.
  productStockingLocations: {
    model_projected_inv_over_lt_ph: { label: 'P90 Cumulative Forecast', short: 'P90 Cumulative Forecast' },
    model_order_constrained: {
      label: 'Recommended Production Quantity (Sale UOM)',
      short: 'Recommended Production Qty',
    },
    model_beginning_available: { label: 'Available On Hand Inventory', short: 'Available On Hand' },
    model_beginning_inflight: { label: 'Inventory in Flight', short: 'Inventory in Flight' },
    model_ending_inflight: { label: 'Inventory in Flight', short: 'Inventory in Flight' },
    fcst_mean: { label: 'Mean forecast', short: 'Mean' },
    fcst_q80: { label: 'SL Target 80%', short: 'SL 80%' },
    fcst_q90: { label: 'SL Target 90%', short: 'SL 90%' },
    fcst_q95: { label: 'SL Target 95%', short: 'SL 95%' },
  },
};

function resolve(key: string, page?: PageKey): FieldMeta | undefined {
  const base = FIELD_LABELS[key];
  const override = page ? PAGE_FIELD_OVERRIDES[page]?.[key] : undefined;
  if (!base && !override) return undefined;
  return { ...(base ?? { label: key }), ...override };
}

// Full metadata (label/short/unit/description), or undefined if the key is unmapped.
export function fieldMeta(key: string, page?: PageKey): FieldMeta | undefined {
  return resolve(key, page);
}

// Full business label; falls back to the raw key so nothing renders blank.
export function fieldLabel(key: string, page?: PageKey): string {
  return resolve(key, page)?.label ?? key;
}

// Compact label for legends/axes; falls back to the full label, then the key.
export function fieldShort(key: string, page?: PageKey): string {
  const m = resolve(key, page);
  return m?.short ?? m?.label ?? key;
}

// Raw backend field/column name to surface on hover ("source"). Centralized so
// the "business label visible, raw source on hover" pattern is tuned in one place
// — e.g. later switch to "label (key)" or a description for future UX polish.
export function fieldSource(key: string): string {
  return key;
}
